import { useMemo, useState } from 'react';
import { WeaveBanner } from '@/components/UI/WeaveBanner/WeaveBanner';
import { WeaveButton } from '@/components/UI/WeaveButton/WeaveButton';
import { WeaveModalElement } from '@/components/UI/WeaveModal/WeaveModal';
import { generateBioScorePdf } from '@/services/export/pdfExport.service';
import {
	buildBioScoreReportData,
	PersistedExportAssets,
	ReportImageData,
} from '@/services/export/reportDataBuilder';
import { WorkflowStage } from '@/types/palette';
import { WorkflowDataMap } from '@/types/workflow';
import styles from './assessmentStyles.module.scss';

type UploadKind = 'windows' | 'daylight';

interface ExportPdfModalContentProps {
	workflowData: WorkflowDataMap;
	currentStage: WorkflowStage;
	initialAssets?: PersistedExportAssets;
	onAssetsChange?: (assets: PersistedExportAssets) => void;
}

const MAX_FILE_SIZE = 6 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

const fileToDataUrl = (file: File) =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(new Error(`Unable to read ${file.name}.`));
		reader.readAsDataURL(file);
	});

const closeModal = () => {
	const modal = document.querySelector('weave-modal') as WeaveModalElement | null;
	modal?.close();
};

const normalizeInitialImages = (
	assets: PersistedExportAssets | undefined,
	kind: UploadKind
): ReportImageData[] => {
	if (!assets) return [];

	if (kind === 'windows') {
		const windowsAssets = assets as PersistedExportAssets & { windowsImage?: ReportImageData | null };
		if (Array.isArray(assets.windowsImages)) return assets.windowsImages;
		return windowsAssets.windowsImage ? [windowsAssets.windowsImage] : [];
	}

	const daylightAssets = assets as PersistedExportAssets & { daylightImage?: ReportImageData | null };
	if (Array.isArray(assets.daylightImages)) return assets.daylightImages;
	return daylightAssets.daylightImage ? [daylightAssets.daylightImage] : [];
};

export const ExportPdfModalContent = ({
	workflowData,
	currentStage,
	initialAssets,
	onAssetsChange,
}: ExportPdfModalContentProps) => {
	const [windowsImages, setWindowsImages] = useState<ReportImageData[]>(
		normalizeInitialImages(initialAssets, 'windows')
	);
	const [daylightImages, setDaylightImages] = useState<ReportImageData[]>(
		normalizeInitialImages(initialAssets, 'daylight')
	);
	const [isExporting, setIsExporting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const hasSavedImages = useMemo(
		() => windowsImages.length > 0 || daylightImages.length > 0,
		[windowsImages.length, daylightImages.length]
	);

	const persistAssets = (nextAssets: PersistedExportAssets) => {
		onAssetsChange?.(nextAssets);
	};

	const handleFileChange = async (kind: UploadKind, files: FileList | null) => {
		if (!files || files.length === 0) return;

		const validFiles = Array.from(files);
		const nextImages: ReportImageData[] = [];

		for (const file of validFiles) {
			if (!ACCEPTED_TYPES.includes(file.type)) {
				setError('Use PNG, JPG or WEBP images for the PDF appendix.');
				return;
			}
			if (file.size > MAX_FILE_SIZE) {
				setError('Each image must be 6 MB or less.');
				return;
			}

			const dataUrl = await fileToDataUrl(file);
			nextImages.push({
				label:
					kind === 'windows' ? 'Windows & Openings Image' : 'Daylight & Views Image',
				fileName: file.name,
				mimeType: file.type,
				dataUrl,
			});
		}

		setError(null);
		if (kind === 'windows') {
			const merged = [...windowsImages, ...nextImages];
			setWindowsImages(merged);
			persistAssets({
				windowsImages: merged,
				daylightImages,
			});
			return;
		}

		const merged = [...daylightImages, ...nextImages];
		setDaylightImages(merged);
		persistAssets({
			windowsImages,
			daylightImages: merged,
		});
	};

	const handleRemoveImage = (kind: UploadKind, index: number) => {
		if (kind === 'windows') {
			const next = windowsImages.filter((_, imageIndex) => imageIndex !== index);
			setWindowsImages(next);
			persistAssets({
				windowsImages: next,
				daylightImages,
			});
			return;
		}

		const next = daylightImages.filter((_, imageIndex) => imageIndex !== index);
		setDaylightImages(next);
		persistAssets({
			windowsImages,
			daylightImages: next,
		});
	};

	const handleExport = async () => {
		try {
			setIsExporting(true);
			setError(null);

			const images = [...windowsImages, ...daylightImages];
			const report = buildBioScoreReportData({
				workflowData,
				currentStage,
				images,
			});
			generateBioScorePdf(report);
			closeModal();
		} catch (exportError) {
			console.error(exportError);
			setError('PDF export failed. Please try again.');
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<div className={styles.exportModalContent}>
			<WeaveBanner
				variant="forma-neutral"
				text="Export uses current BioScore workflow data. Uploaded images are optional and remain saved for the next export."
			/>
			{error ? <div className={styles.exportError}>{error}</div> : null}
			{hasSavedImages ? (
				<div className={styles.exportHint}>Previously uploaded images are already loaded.</div>
			) : null}
			<div className={styles.uploadGrid}>
				<div className={styles.uploadCard}>
					<div className={styles.uploadTitle}>Windows & Openings images</div>
					<label className={styles.uploadField}>
						<input
							type="file"
							accept=".png,.jpg,.jpeg,.webp"
							multiple
							onChange={(event) =>
								handleFileChange('windows', event.target.files)
							}
						/>
					</label>
					{windowsImages.length > 0 ? (
						<div className={styles.previewList}>
							{windowsImages.map((image, index) => (
								<div key={`${image.fileName}-${index}`} className={styles.previewCard}>
									<img
										src={image.dataUrl}
										alt={image.fileName}
										className={styles.previewImage}
									/>
									<div className={styles.previewMeta}>{image.fileName}</div>
									<WeaveButton
										variant="outlined"
										density="medium"
										onClick={() => handleRemoveImage('windows', index)}
									>
										Remove
									</WeaveButton>
								</div>
							))}
						</div>
					) : (
						<div className={styles.uploadHint}>
							Optional. Add one or more manual screenshots.
						</div>
					)}
				</div>
				<div className={styles.uploadCard}>
					<div className={styles.uploadTitle}>Daylight & Views images</div>
					<label className={styles.uploadField}>
						<input
							type="file"
							accept=".png,.jpg,.jpeg,.webp"
							multiple
							onChange={(event) =>
								handleFileChange('daylight', event.target.files)
							}
						/>
					</label>
					{daylightImages.length > 0 ? (
						<div className={styles.previewList}>
							{daylightImages.map((image, index) => (
								<div key={`${image.fileName}-${index}`} className={styles.previewCard}>
									<img
										src={image.dataUrl}
										alt={image.fileName}
										className={styles.previewImage}
									/>
									<div className={styles.previewMeta}>{image.fileName}</div>
									<WeaveButton
										variant="outlined"
										density="medium"
										onClick={() => handleRemoveImage('daylight', index)}
									>
										Remove
									</WeaveButton>
								</div>
							))}
						</div>
					) : (
						<div className={styles.uploadHint}>
							Optional. Add one or more manual screenshots.
						</div>
					)}
				</div>
			</div>
			<div className={styles.exportModalActions}>
				<WeaveButton variant="outlined" onClick={closeModal} disabled={isExporting}>
					Cancel
				</WeaveButton>
				<WeaveButton variant="solid" onClick={handleExport} disabled={isExporting}>
					{isExporting ? 'Generating...' : 'Generate PDF'}
				</WeaveButton>
			</div>
		</div>
	);
};
